import {useCallback, useEffect} from "react";
import PlayerTableView from "@/player/view/PlayerTableView.tsx";
import {PlayerDisplay} from "@/player/PlayerViewHooks.ts";
import PlayerCampaignView from "@/player/view/PlayerCampaignView.tsx";
import TableTopRuntimeBoundary from "@/tabletop/TableTopRuntimeBoundary.tsx";
import PlayerSceneBoundary from "@/player/view/PlayerSceneBoundary.tsx";
import {playerDisplayKey} from "@/player/view/usePlayerViewTransition.ts";

interface PlayerDisplaySurfaceProps {
    display: PlayerDisplay;
    loading: boolean;
    error: boolean;
    onReady?: (key: string) => void;
    onError?: (key: string) => void;
}

function CampaignSurface({loading, error, onReady}: {
    loading: boolean;
    error: boolean;
    onReady?: () => void;
}) {
    useEffect(() => onReady?.(), [onReady]);
    return <PlayerCampaignView loading={loading} error={error}/>;
}

export default function PlayerDisplaySurface({
    display,
    loading,
    error,
    onReady,
    onError,
}: PlayerDisplaySurfaceProps) {
    const key = playerDisplayKey(display);
    const ready = useCallback(() => onReady?.(key), [key, onReady]);
    const failed = useCallback(() => onError?.(key), [key, onError]);

    if (display.display === "campaign") {
        return <CampaignSurface loading={loading} error={error} onReady={onReady ? ready : undefined}/>;
    }
    if (display.display === "encounter") {
        return <TableTopRuntimeBoundary
            encounterId={display.encounter.id}
            onReady={onReady ? ready : undefined}
            onError={onError ? failed : undefined}
        >
            <PlayerTableView/>
        </TableTopRuntimeBoundary>;
    }
    return <PlayerSceneBoundary
        sceneId={display.scene.id}
        onReady={onReady ? ready : undefined}
        onError={onError ? failed : undefined}
    />;
}
